import React, { useState, useEffect, useCallback } from 'react'
import { token } from '@atlaskit/tokens'
import Avatar from '@atlaskit/avatar'
import Button from '@atlaskit/button'
import DropdownMenu, { DropdownItemGroup, DropdownItem } from '@atlaskit/dropdown-menu'
import TextArea from '@atlaskit/textarea'
import Spinner from '@atlaskit/spinner'
import EditorMoreIcon from '@atlaskit/icon/glyph/editor/more'
import { formatRelativeTime, wasCommentEdited, MAX_COMMENT_LENGTH } from '../../utils/formatters'
import { getUserDetails, getCachedUserDetails } from '../../utils/userCache'

/**
 * COMMENT ITEM COMPONENT
 *
 * Displays a single comment with author info, timestamp, and content.
 * Supports inline editing and deletion if user has permissions.
 *
 * Features:
 * - Author avatar and name (fetched from Jira API)
 * - Relative timestamp with "(edited)" indicator
 * - Three-dot menu with Edit/Delete options (based on canEdit/canDelete)
 * - Inline edit mode with save/cancel
 *
 * @param {object} comment - Comment object from API
 * @param {function} onEdit - Callback when comment is edited (receives commentId, newContent)
 * @param {function} onDelete - Callback when comment is deleted (receives commentId)
 */
export function CommentItem({ comment, onEdit, onDelete }) {
  // State for user details (fetched from Jira)
  const [userDetails, setUserDetails] = useState(() => {
    // Try to get from cache first for immediate render
    return getCachedUserDetails(comment?.createdBy?.accountId)
  })

  // State for edit mode
  const [isEditing, setIsEditing] = useState(false)
  const [editContent, setEditContent] = useState('')
  const [isSaving, setIsSaving] = useState(false)
  const [isDeleting, setIsDeleting] = useState(false)

  // Extract author accountId from comment
  const authorAccountId = comment?.createdBy?.accountId

  /**
   * FETCH USER DETAILS
   * Fetches user details from Jira API on mount.
   */
  useEffect(() => {
    if (authorAccountId) {
      getUserDetails(authorAccountId).then(details => {
        if (details) {
          setUserDetails(details)
        }
      })
    }
  }, [authorAccountId])

  /**
   * ENTER EDIT MODE
   * Opens inline editor with current content.
   */
  const handleStartEdit = useCallback(() => {
    setEditContent(comment.content || '')
    setIsEditing(true)
  }, [comment.content])

  /**
   * CANCEL EDIT
   * Closes inline editor without saving.
   */
  const handleCancelEdit = useCallback(() => {
    setIsEditing(false)
    setEditContent('')
  }, [])

  /**
   * SAVE EDIT
   * Saves the edited comment content.
   */
  const handleSaveEdit = useCallback(async () => {
    // Don't save if content is empty
    if (!editContent.trim()) return

    // Don't save if content hasn't changed
    if (editContent.trim() === comment.content) {
      handleCancelEdit()
      return
    }

    setIsSaving(true)
    try {
      await onEdit(comment.id, editContent.trim())
      setIsEditing(false)
      setEditContent('')
    } catch (error) {
      console.error('Failed to save comment:', error)
      // Keep edit mode open on error
    } finally {
      setIsSaving(false)
    }
  }, [editContent, comment.id, comment.content, onEdit, handleCancelEdit])

  /**
   * HANDLE DELETE
   * Deletes the comment after confirmation.
   */
  const handleDelete = useCallback(async () => {
    // Simple confirmation
    if (!window.confirm('Are you sure you want to delete this comment? This action cannot be undone.')) {
      return
    }

    setIsDeleting(true)
    try {
      await onDelete(comment.id)
    } catch (error) {
      console.error('Failed to delete comment:', error)
    } finally {
      setIsDeleting(false)
    }
  }, [comment.id, onDelete])

  /**
   * HANDLE KEYBOARD SHORTCUT
   * Cmd/Ctrl+Enter to save, Escape to cancel
   */
  const handleKeyDown = useCallback((e) => {
    if ((e.metaKey || e.ctrlKey) && e.key === 'Enter') {
      e.preventDefault()
      handleSaveEdit()
    } else if (e.key === 'Escape') {
      e.preventDefault()
      handleCancelEdit()
    }
  }, [handleSaveEdit, handleCancelEdit])

  // Show loading state if deleting
  if (isDeleting) {
    return (
      <div
        className="comment-item"
        style={{
          padding: '12px',
          borderRadius: '4px',
          backgroundColor: token('color.background.neutral', '#F4F5F7'),
          marginBottom: '8px',
          opacity: 0.5,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          minHeight: '60px'
        }}
      >
        <Spinner size="small" />
        <span style={{ marginLeft: '8px', color: token('color.text.subtle') }}>Deleting...</span>
      </div>
    )
  }

  // Determine display name and avatar
  const displayName = userDetails?.displayName || 'Unknown User'
  const avatarUrl = userDetails?.avatarUrl || null

  // Check if comment was edited
  const edited = wasCommentEdited(comment.createdOn, comment.updatedOn)

  return (
    <div
      className="comment-item"
      style={{
        padding: '12px',
        borderRadius: '4px',
        backgroundColor: token('color.background.neutral', '#F4F5F7'),
        marginBottom: '8px'
      }}
    >
      {/* Header: Avatar + Name + Timestamp + Menu */}
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          marginBottom: '8px'
        }}
      >
        {/* Left: Avatar + Name + Time */}
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          <Avatar
            size="small"
            src={avatarUrl}
            name={displayName}
          />
          <span
            style={{
              fontSize: '13px',
              fontWeight: 500,
              color: token('color.text', '#172B4D')
            }}
          >
            {displayName}
          </span>
          <span
            style={{
              fontSize: '12px',
              color: token('color.text.subtle', '#5E6C84')
            }}
          >
            · {formatRelativeTime(comment.createdOn)}
            {edited && (
              <span style={{ fontStyle: 'italic' }}> (edited)</span>
            )}
          </span>
        </div>

        {/* Right: Actions menu (only show if user has permissions) */}
        {(comment.canEdit || comment.canDelete) && !isEditing && (
          <DropdownMenu
            trigger={({ triggerRef, ...props }) => (
              <Button
                {...props}
                ref={triggerRef}
                appearance="subtle"
                spacing="compact"
                iconBefore={<EditorMoreIcon size="small" label="More actions" />}
              />
            )}
            placement="bottom-end"
            shouldRenderToParent
          >
            <DropdownItemGroup>
              {comment.canEdit && (
                <DropdownItem onClick={handleStartEdit}>
                  Edit
                </DropdownItem>
              )}
              {comment.canDelete && (
                <DropdownItem onClick={handleDelete}>
                  Delete
                </DropdownItem>
              )}
            </DropdownItemGroup>
          </DropdownMenu>
        )}
      </div>

      {/* Content: Edit mode or read mode */}
      {isEditing ? (
        <div style={{ marginTop: '8px' }}>
          <TextArea
            value={editContent}
            onChange={(e) => setEditContent(e.target.value)}
            onKeyDown={handleKeyDown}
            minimumRows={2}
            maxLength={MAX_COMMENT_LENGTH}
            placeholder="Write a comment..."
            autoFocus
          />
          <div
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              marginTop: '8px'
            }}
          >
            <span
              style={{
                fontSize: '11px',
                color: token('color.text.subtlest', '#7A869A')
              }}
            >
              {MAX_COMMENT_LENGTH - editContent.length} characters remaining
            </span>
            <div style={{ display: 'flex', gap: '8px' }}>
              <Button
                appearance="subtle"
                spacing="compact"
                onClick={handleCancelEdit}
                isDisabled={isSaving}
              >
                Cancel
              </Button>
              <Button
                appearance="primary"
                spacing="compact"
                onClick={handleSaveEdit}
                isLoading={isSaving}
                isDisabled={!editContent.trim() || isSaving}
              >
                Save
              </Button>
            </div>
          </div>
        </div>
      ) : (
        <div
          style={{
            fontSize: '13px',
            color: token('color.text', '#172B4D'),
            lineHeight: '1.5',
            whiteSpace: 'pre-wrap',
            wordBreak: 'break-word'
          }}
        >
          {comment.content}
        </div>
      )}
    </div>
  )
}

export default CommentItem
